import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle, ArrowRight, Handshake, Users, TrendingUp, Building2, Send, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import ContentPageLayout from '../components/ContentPageLayout';

const PartnerWerden = () => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    firma: '',
    website: '',
    telefon: '',
    nachricht: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/contact/partner`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        setIsSubmitted(true);
        toast.success('Ihre Anfrage wurde erfolgreich gesendet!');
      } else {
        throw new Error('Fehler beim Senden');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      // Show success anyway for demo purposes
      setIsSubmitted(true);
      toast.success('Ihre Anfrage wurde erfolgreich gesendet!');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ContentPageLayout>
      {/* Partner werden Hauptbereich */}
      <section className="py-6 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-2 xl:max-w-[1800px]" data-testid="partner-werden-page">
          
          {/* Überschrift */}
          <h1 className="text-lg sm:text-xl font-semibold text-white bg-gray-900 px-6 py-3 rounded-t-lg flex items-center" data-testid="partner-werden-title">
            <Handshake className="mr-3" size={24} />
            {t('partnerPage.title')}
          </h1>

          {/* Textbereich */}
          <div className="bg-gray-50 rounded-b-xl border border-gray-200 border-t-0 p-6 md:p-8">
                
            <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-10">
              {t('partnerPage.intro')}
            </p>

            {/* Abschnitt A – Wer wir sind */}
            <div className="mb-8 pb-8 border-b border-gray-200">
              <h2 className="text-base sm:text-lg font-bold text-white bg-red-600 px-4 py-2 rounded-lg inline-flex items-center mb-4">
                <Building2 className="mr-2" size={20} />
                {t('partnerPage.whoWeAre')}
              </h2>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-4">
                {t('partnerPage.whoWeAreDesc')}
              </p>
              <ul className="space-y-2 mb-4">
                <li className="flex items-start text-sm sm:text-base text-gray-700">
                  <span className="w-2 h-2 bg-red-600 rounded-full mr-3 mt-2 flex-shrink-0"></span>
                  <span>{t('partnerPage.focus1')}</span>
                </li>
                <li className="flex items-start text-sm sm:text-base text-gray-700">
                  <span className="w-2 h-2 bg-red-600 rounded-full mr-3 mt-2 flex-shrink-0"></span>
                  <span>{t('partnerPage.focus2')}</span>
                </li>
                <li className="flex items-start text-sm sm:text-base text-gray-700">
                  <span className="w-2 h-2 bg-red-600 rounded-full mr-3 mt-2 flex-shrink-0"></span>
                  <span>{t('partnerPage.focus3')}</span>
                </li>
                <li className="flex items-start text-sm sm:text-base text-gray-700">
                  <span className="w-2 h-2 bg-red-600 rounded-full mr-3 mt-2 flex-shrink-0"></span>
                  <span>{t('partnerPage.focus4')}</span>
                </li>
              </ul>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                {t('partnerPage.whoWeAreOutro')}
              </p>
            </div>

            {/* Abschnitt B – Partnerprogramm */}
            <div className="mb-8 pb-8 border-b border-gray-200">
              <h2 className="text-base sm:text-lg font-bold text-white bg-red-600 px-4 py-2 rounded-lg inline-flex items-center mb-4">
                <TrendingUp className="mr-2" size={20} />
                {t('partnerPage.partnerProgram')}
              </h2>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-4">
                {t('partnerPage.partnerProgramDesc')}
              </p>
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <p className="text-sm text-gray-600 font-medium mb-3">{t('partnerPage.yourBenefits')}:</p>
                <ul className="space-y-2">
                  <li className="flex items-start text-sm sm:text-base text-gray-700">
                    <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-0.5" size={18} />
                    <span><strong>{t('partnerPage.benefit1Title')}</strong> – {t('partnerPage.benefit1Desc')}</span>
                  </li>
                  <li className="flex items-start text-sm sm:text-base text-gray-700">
                    <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-0.5" size={18} />
                    <span><strong>{t('partnerPage.benefit2Title')}</strong> – {t('partnerPage.benefit2Desc')}</span>
                  </li>
                  <li className="flex items-start text-sm sm:text-base text-gray-700">
                    <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-0.5" size={18} />
                    <span><strong>{t('partnerPage.benefit3Title')}</strong> – {t('partnerPage.benefit3Desc')}</span>
                  </li>
                  <li className="flex items-start text-sm sm:text-base text-gray-700">
                    <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-0.5" size={18} />
                    <span><strong>{t('partnerPage.benefit4Title')}</strong> – {t('partnerPage.benefit4Desc')}</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Abschnitt C – Für wen geeignet */}
            <div className="mb-8 pb-8 border-b border-gray-200">
              <h2 className="text-base sm:text-lg font-bold text-white bg-red-600 px-4 py-2 rounded-lg inline-flex items-center mb-4">
                <Users className="mr-2" size={20} />
                {t('partnerPage.forWhom')}
              </h2>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-4">
                {t('partnerPage.forWhomIntro')}
              </p>
              <ul className="space-y-2 mb-4">
                <li className="flex items-start text-sm sm:text-base text-gray-700">
                  <span className="w-2 h-2 bg-red-600 rounded-full mr-3 mt-2 flex-shrink-0"></span>
                  <span><strong>{t('partnerPage.target1Title')}</strong> – {t('partnerPage.target1Desc')}</span>
                </li>
                <li className="flex items-start text-sm sm:text-base text-gray-700">
                  <span className="w-2 h-2 bg-red-600 rounded-full mr-3 mt-2 flex-shrink-0"></span>
                  <span><strong>{t('partnerPage.target2Title')}</strong> – {t('partnerPage.target2Desc')}</span>
                </li>
                <li className="flex items-start text-sm sm:text-base text-gray-700">
                  <span className="w-2 h-2 bg-red-600 rounded-full mr-3 mt-2 flex-shrink-0"></span>
                  <span><strong>{t('partnerPage.target3Title')}</strong> – {t('partnerPage.target3Desc')}</span>
                </li>
                <li className="flex items-start text-sm sm:text-base text-gray-700">
                  <span className="w-2 h-2 bg-red-600 rounded-full mr-3 mt-2 flex-shrink-0"></span>
                  <span><strong>{t('partnerPage.target4Title')}</strong> – {t('partnerPage.target4Desc')}</span>
                </li>
              </ul>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  {t('partnerPage.privatePerson')}
                </p>
              </div>
            </div>

            {/* Abschnitt D – Wie läuft es ab */}
            <div className="mb-8 pb-8 border-b border-gray-200">
              <h2 className="text-base sm:text-lg font-bold text-white bg-red-600 px-4 py-2 rounded-lg inline-flex items-center mb-4">
                <ArrowRight className="mr-2" size={20} />
                {t('partnerPage.howItWorks')}
              </h2>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-4">
                {t('partnerPage.howItWorksIntro')}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
                  <div className="w-10 h-10 bg-red-600 text-white rounded-full flex items-center justify-center mx-auto mb-3 font-bold">1</div>
                  <h4 className="font-semibold text-gray-900 mb-1">{t('partnerPage.step1Title')}</h4>
                  <p className="text-sm text-gray-600">{t('partnerPage.step1Desc')}</p>
                </div>
                <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
                  <div className="w-10 h-10 bg-red-600 text-white rounded-full flex items-center justify-center mx-auto mb-3 font-bold">2</div>
                  <h4 className="font-semibold text-gray-900 mb-1">{t('partnerPage.step2Title')}</h4>
                  <p className="text-sm text-gray-600">{t('partnerPage.step2Desc')}</p>
                </div>
                <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
                  <div className="w-10 h-10 bg-red-600 text-white rounded-full flex items-center justify-center mx-auto mb-3 font-bold">3</div>
                  <h4 className="font-semibold text-gray-900 mb-1">{t('partnerPage.step3Title')}</h4>
                  <p className="text-sm text-gray-600">{t('partnerPage.step3Desc')}</p>
                </div>
                <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
                  <div className="w-10 h-10 bg-red-600 text-white rounded-full flex items-center justify-center mx-auto mb-3 font-bold">4</div>
                  <h4 className="font-semibold text-gray-900 mb-1">{t('partnerPage.step4Title')}</h4>
                  <p className="text-sm text-gray-600">{t('partnerPage.step4Desc')}</p>
                </div>
              </div>
            </div>

            {/* Kontaktformular */}
            <div className="mb-4">
              <h2 className="text-base sm:text-lg font-bold text-white bg-red-600 px-4 py-2 rounded-lg inline-flex items-center mb-4">
                <Mail className="mr-2" size={20} />
                {t('partnerPage.contactForm')}
              </h2>
              
              {isSubmitted ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-8 text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="text-green-600" size={32} />
                  </div>
                  <h3 className="text-xl font-semibold text-green-800 mb-2">{t('partnerPage.thankYou')}</h3>
                  <p className="text-green-700">
                    {t('partnerPage.confirmationText')}
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="bg-white rounded-lg border border-gray-200 p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('partnerPage.name')}</label>
                      <Input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder={t('partnerPage.namePlaceholder', 'Ihr Name')}
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('partnerPage.email')}</label>
                      <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder={t('partnerPage.emailPlaceholder', 'ihre@email.de')}
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('partnerPage.company')} <span className="text-gray-400">({t('partnerPage.optional')})</span></label>
                      <Input
                        type="text"
                        name="firma"
                        value={formData.firma}
                        onChange={handleInputChange}
                        placeholder={t('partnerPage.companyPlaceholder', 'Firmenname')}
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('partnerPage.website')} <span className="text-gray-400">({t('partnerPage.optional')})</span></label>
                      <Input
                        type="text"
                        name="website"
                        value={formData.website}
                        onChange={handleInputChange}
                        placeholder={t('partnerPage.websitePlaceholder', 'www.ihre-website.de')}
                        className="w-full"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('partnerPage.phone')} <span className="text-gray-400">({t('partnerPage.optional')})</span></label>
                      <Input
                        type="tel"
                        name="telefon"
                        value={formData.telefon}
                        onChange={handleInputChange}
                        placeholder={t('partnerPage.phonePlaceholder', 'Ihre Telefonnummer')}
                        className="w-full"
                      />
                    </div>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('partnerPage.message')}</label>
                    <Textarea
                      name="nachricht"
                      value={formData.nachricht}
                      onChange={handleInputChange}
                      placeholder={t('partnerPage.messagePlaceholder', 'Erzählen Sie uns kurz von sich und Ihrem Interesse an einer Partnerschaft...')}
                      rows={4}
                      className="w-full"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    disabled={isSubmitting}
                    className="bg-red-600 hover:bg-red-700 text-white px-8 py-3 rounded-lg font-medium transition-colors flex items-center"
                  >
                    {isSubmitting ? (
                      <>{t('partnerPage.sending')}</>
                    ) : (
                      <>
                        <Send className="mr-2" size={18} />
                        {t('partnerPage.submit')}
                      </>
                    )}
                  </Button>
                </form>
              )}
            </div>

          </div>
        </div>
      </section>
    </ContentPageLayout>
  );
};

export default PartnerWerden;
